import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconEdit, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { labelFor, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import usePaginatedList from '../hooks/usePaginatedList';

interface EmployerRow {
  id: number;
  name: string;
  registration_number: string;
  industry_sector: string | null;
  status: 'active' | 'dormant' | 'deregistered';
  latest_contribution_schedule: { id: number; status: 'draft' | 'submitted' | 'receipted' } | null;
}

const STATUS_BADGE: Record<string, string> = {
  active: 'badge-active',
  dormant: 'badge-neutral',
  deregistered: 'badge-urgent',
};

const SCHEDULE_STATUS_BADGE: Record<string, string> = {
  draft: 'badge-scheduled',
  submitted: 'badge-neutral',
  receipted: 'badge-active',
};

export function Employers() {
  const { lists } = useConfig();
  const { can } = useAuth();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [search, setSearch] = useState('');

  const canCreate = can('memberships.create');
  const canUpdate = can('memberships.update');
  const canArchive = can('memberships.archive');

  const filters = useMemo(() => ({ search: search || undefined }), [search]);
  const { data, loading, error, setPage, reload: load } = usePaginatedList<EmployerRow>('/employers', filters);

  const remove = async (employer: EmployerRow) => {
    if (!(await confirmDelete(`Delete ${employer.name}?`))) return;
    try {
      await api.delete(`/employers/${employer.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Employers</h1>
        {canCreate && (
          <Link className="btn btn-primary" to="/memberships/employers/new">
            + Add
          </Link>
        )}
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search name or registration number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Registration No.</th>
                <th>Sector</th>
                <th>Status</th>
                <th>Latest Schedule</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <strong>{e.name}</strong>
                    </td>
                    <td className="muted">{e.registration_number}</td>
                    <td>{e.industry_sector ? labelFor(lists, 'industry_sector', e.industry_sector) : '—'}</td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[e.status] ?? 'badge-neutral'}`}>{e.status}</span>
                    </td>
                    <td>
                      {e.latest_contribution_schedule ? (
                        <span className={`badge ${SCHEDULE_STATUS_BADGE[e.latest_contribution_schedule.status]}`}>
                          {e.latest_contribution_schedule.status}
                        </span>
                      ) : (
                        <span className="muted">None yet</span>
                      )}
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/memberships/employers/${e.id}`} />
                      {canUpdate && <IconButton icon={IconEdit} label="Edit" to={`/memberships/employers/${e.id}/edit`} />}
                      {canArchive && (
                        <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(e)} />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No employers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
